import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { CustomsClearanceEndpointService } from '../../clearance.service';
import { CustomsClearanceService } from '../../models/customs-clearance-service.model';
import { CustomsClearanceServiceVariant } from '../../models/customs-clearance-service-variant.model';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';

@Component({
    selector: 'app-add-edit-services',
    templateUrl: './add-edit-clearance-services.component.html',
    styleUrls: ['./add-edit-clearance-services.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ToastModule,
        ProgressBarModule,
        ButtonModule,
        InputTextModule,
        InputNumberModule,
        TextareaModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        TableModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent
    ],
    providers: [MessageService, CustomsClearanceEndpointService, SessionStorageService]
})
export class AddEditClearanceServicesComponent implements OnInit {
    serviceForm!: FormGroup;
    serviceId?: number;
    isEditMode = false;
    isLoading = false;
    variants: CustomsClearanceServiceVariant[] = [];

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private messageService: MessageService,
        private clearanceService: CustomsClearanceEndpointService,
        private sessionStorageService: SessionStorageService
    ) {}

    ngOnInit(): void {
        this.serviceForm = this.fb.group({
            serviceID: [null],
            serviceName: ['', Validators.required],
            description: [''],
            buyingAmount: [0],
            sellingAmount: [0],
            owningCompanyId: [this.sessionStorageService.getCompany()?.id]
        });

        this.route.params.subscribe(params => {
            const id = params['id'];
            if (id) {
                this.serviceId = Number(id);
                this.isEditMode = true;
                this.loadService(this.serviceId);
            }
        });
    }

    loadService(id: number) {
        this.isLoading = true;
        this.clearanceService.getCustomsClearanceServiceById(id).subscribe({
            next: service => {
                this.serviceForm.patchValue(service);
                this.variants = service.variants || [];
                this.isLoading = false;
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error loading clearance service' });
                this.isLoading = false;
            }
        });
    }

    save() {
        if (this.serviceForm.invalid) {
            this.serviceForm.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Missing Data', detail: 'Service name is required.' });
            return;
        }

        this.isLoading = true;
        const payload: CustomsClearanceService = {
            ...this.serviceForm.value,
            variants: this.variants,
            owningCompanyId: this.serviceForm.value.owningCompanyId || this.sessionStorageService.getCompany()?.id
        };

        const request = this.isEditMode && this.serviceId
            ? this.clearanceService.updateCustomsClearanceService(this.serviceId, payload)
            : this.clearanceService.createCustomsClearanceService(payload);

        request.subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: this.isEditMode ? 'Clearance service updated' : 'Clearance service created'
                });
                this.isLoading = false;
                this.router.navigate(['/clearance/services']);
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: this.isEditMode ? 'Error updating clearance service' : 'Error creating clearance service'
                });
                this.isLoading = false;
            }
        });
    }

    cancel() {
        this.router.navigate(['/clearance/services']);
    }

    formatAmount(value?: number): string {
        return new Intl.NumberFormat('en-SA', { style: 'currency', currency: 'SAR' }).format(Number(value || 0));
    }
}
